/**
 * `NowPlaying` — the screen the product is judged by. `docs/design-direction.md`
 * §8.1 for the art direction and §7.3 for THE PULL UP.
 *
 * **The one loud object is the dubplate** (§2, §12.13). Everything else on this
 * screen is a 1px printed rule, a mono caps label or a tabular numeral: the 3px
 * accent rule across the very top, the eyebrow, the crop-marked catalogue plate with
 * its registration crosshair, the `--fs-34` title carrying the screen's single
 * misregistration ghost, the one-drop ruler, the 56px waveform band with its flanking
 * timecodes, the downbeat rule, the spec table, the amp faceplate, the halftone-filled
 * archive numeral bleeding off the right edge, and one edge-cropped stamp.
 *
 * ## What this screen does NOT do
 *
 *  · **It never fabricates.** No em-dashed metadata (`SpecTable` omits an absent row),
 *    no synthesised waveform (`Waveform` renders the designed bar-ruler state when the
 *    server has no peaks and there is no code path here that could invent one), no
 *    invented counter. The eyebrow's position number is the real 1-based index in the
 *    set and is simply absent when there is no set (§8.8, §12.6).
 *  · **It never drives progress from `requestAnimationFrame`.** rAF stops firing in a
 *    backgrounded webview, which is exactly when this app is doing its most important
 *    work. The playhead comes from `store/player.positionMs`, which the engine writes
 *    from `timeupdate` plus its own interval (spec.md §13.3). The only per-frame
 *    writes on this screen are made by a pointer handler at pointer rate — the pull-up
 *    angle and `currentTime` — and a pointer handler only exists while a finger is
 *    down.
 *  · **It never shows an empty box while metadata loads** (§8.8). The queue already
 *    carries a `TrackSummary` — title, performer, plate seed, catalogue number,
 *    duration, BPM, year — so the record, the label print and the transport render
 *    immediately and the `TrackDetail` fills in the key, the riddim, the label and the
 *    peaks when it lands. A failed detail fetch degrades the spec table; it does not
 *    blank a track that is playing perfectly well.
 *
 * ## THE PULL UP (§7.3), and the three things that make it ship
 *
 * Reverse playback is impossible with `HTMLAudioElement` and `playbackRate` cannot go
 * negative, so the ritual is implemented as a **ducked backward scrub**: the record
 * follows the thumb 1:1, `currentTime` walks backwards at 1.333 s of audio per
 * revolution (45 RPM, matching the rim print), the gain ducks to
 * {@link DUCK_VOLUME} rather than muting — you hear the scrub, which is the whole
 * point — and **while the thumb is moving** the rate drops to {@link SCRUB_RATE} with
 * `preservesPitch` off so the pitch falls like a hand on the platter. A thumb that is
 * merely resting stops the record instead ({@link HELD_RATE}, {@link HOLD_SETTLE_MS}):
 * contact holds, movement scratches. **There is no Web Audio graph** (§7.3,
 * §9.6): Telegram's audio paths frequently deny an `AudioContext`, so the duck is
 * `audio.volume` through `wiring.setVolume`, which keeps the store honest too.
 *
 *  1. `disableVerticalSwipes()` on pointer-down and `enableVerticalSwipes()` on
 *     **every** exit path — release, cancel, lost capture, and unmount mid-drag — or
 *     Telegram's sheet-dismiss gesture steals the drag and the user is left in an app
 *     that cannot be closed the way the platform trained them to close it. That
 *     pairing is asserted four times over in the suite.
 *  2. `touch-action: none` on the disc, which `Disc`'s mount and `texture.css`'s
 *     `.disc` both already declare.
 *  3. **The reduced-motion fallback is not optional** (§12.23). With
 *     `prefers-reduced-motion: reduce` the drag handlers are not installed at all and
 *     the `PULL UP!` stamp is a real focusable button with a Russian accessible name
 *     that restarts the track. The ritual survives; the inertia does not.
 *
 * The record **freezes while the thumb is down**: the disc's `playing` goes false for
 * the duration of the drag, which pauses the revolution mid-groove exactly as
 * `Disc`'s contract requires and lets the 1:1 rotation read as the hand holding the
 * platter rather than as two rotations fighting.
 *
 * ## Two places where this file resolves a conflict in the contract
 *
 *  · **One stamp bed, not two.** §6.13 allows three blend-mode elements per screen and
 *    spends them on the grain layer, the ghost and *one* of {stamp, marker, art
 *    duotone}. The grain layer belongs to the shell, so this screen may print the
 *    ghost, one stamp and — when the channel supplied artwork — the plate's duotone.
 *    Two stamps at once would break that arithmetic, so the bottom-right bed prints
 *    **one** stamp whose copy depends on state: `PULL UP!` while a pull-up is landing
 *    (and permanently, as a button, under reduced motion), `DUBPLATE SPECIAL` at rest.
 *  · **The marquee is one pass over the 3-line clamp.** §4.3 clamps this title to three
 *    lines; §7.2 gives an over-long title one single pass after a 1200 ms hold and then
 *    truncates. Both hold here: the title is clamped, and when the clamp is actually
 *    hiding something the block makes one transform pass that reveals the tail and
 *    returns to the truncated resting state. `animation-iteration-count: 1` is written
 *    out as its own declaration — an infinite marquee is banned outright (§12.19),
 *    because a crate does not fidget.
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, JSX, PointerEvent as ReactPointerEvent } from 'react';

import { decodePeaks, hasCyrillic } from '@bl/shared';
import type { TrackDetail, TrackSummary } from '@bl/shared';

import { buildPath, navigate } from '../app/router';
import type { RouteName, RouteParams } from '../app/router';
import { Empty } from '../app/states/Empty';
import { ErrorState } from '../app/states/ErrorState';
import { Loading, StateFrame } from '../app/states/Loading';
import { getWiring } from '../app/wiring';
import type { Wiring } from '../app/wiring';
import { Disc } from '../components/Disc';
import { FileBracket } from '../components/FileBracket';
import { PlateLabel } from '../components/PlateLabel';
import { RimText } from '../components/RimText';
import { Scrub, formatTimecode } from '../components/Scrub';
import { Transport } from '../components/Transport';
import type { TransportSpeed } from '../components/Transport';
import { CropPlate } from '../components/primitives/CropPlate';
import { DownbeatRule } from '../components/primitives/DownbeatRule';
import { Ghost } from '../components/primitives/Ghost';
import { Halftone } from '../components/primitives/Halftone';
import { cx } from '../components/primitives/Rule';
import { Ruler } from '../components/primitives/Ruler';
import { Silkscreen } from '../components/primitives/Silkscreen';
import { SpecTable } from '../components/primitives/SpecTable';
import type { SpecRow } from '../components/primitives/SpecTable';
import { Stamp } from '../components/primitives/Stamp';
import { audio } from '../engine/element';
import { api } from '../lib/api';
import type { ApiClient } from '../lib/api';
import { telegram as defaultTelegram } from '../lib/telegram';
import type { TelegramFacade } from '../lib/telegram';
import { selectIsFavorite, useLibraryStore } from '../store/library';
import { selectEffectiveDurationMs, usePlayerStore } from '../store/player';
import { selectCurrentTrack, useQueueStore } from '../store/queue';

import styles from './nowplaying.module.css';

/* ══ constants ═════════════════════════════════════════════════════════════════════ */

/** One revolution of a 45 is 1.333 s of audio; the rotation follows the thumb 1:1. */
export const AUDIO_PER_REV = 1.333;

/**
 * Past this much anticlockwise travel, the release is a pull-up.
 *
 * **A DELIBERATE DEVIATION FROM §7.3, which writes `-45`.** Operator report,
 * 2026-09-05, from a real phone: "если отпускаешь пластинку после скретча, трек заново
 * начинает играть". −45° is one EIGHTH of a turn — 45/360 × 1.333 = 0.167 s of audio —
 * which every real scratch clears in its first few milliseconds. So every scratch was
 * classified as a pull-up and every release called {@link Commands.restart}, i.e.
 * `seekTo(0)`. The signature gesture of the app had eaten the ordinary one.
 *
 * −540° is a turn and a half: a shove, held, the way a selector actually pulls a tune
 * up. It cannot be reached by accident, and it is unmistakable on the way there, now
 * that the record tracks the thumb 1:1 (`rotate: var(--angle)`, `nowplaying.module.css`).
 */
export const PULL_UP_THRESHOLD_DEG = -540;

/**
 * …and a gesture that also travelled this far CLOCKWISE was a scratch, not a pull-up.
 *
 * `delta` is a NET signed accumulation, so a back-and-forth scratch that happens to end
 * 540° anticlockwise of where it started scores identically to one deliberate
 * anticlockwise shove. The net says where the thumb ended; `forward` says how it got
 * there. Both have to agree before a track is thrown back to zero.
 */
export const PULL_UP_MAX_FORWARD_DEG = 90;

/**
 * Duck, do not mute — you have to hear the scrub for the gesture to make sense.
 *
 * Was 0.15, which is ≈ −16 dB of linear amplitude: on a phone speaker in a room with
 * any noise in it, "duck" and "mute" are the same number. On iOS it was neither —
 * WebKit ignores writes to `HTMLMediaElement.volume` outright, so the duck has never
 * happened on an iPhone at all. Raising it fixes Android and desktop and changes
 * nothing on iOS, where the gain was already full.
 */
export const DUCK_VOLUME = 0.45;

/**
 * The platter is dragged, so the deck runs slow with the pitch let go.
 *
 * 0.8 rather than §7.3's 0.5: with the seek throttled ({@link SCRUB_SEEK_INTERVAL_MS})
 * each window has to render enough audio to be *heard* before the next jump, and 0.5
 * sits on WebKit's historical lower bound for audible varispeed. The pitch still falls
 * — that is what `preservesPitch = false` is for — just less far.
 */
export const SCRUB_RATE = 0.8;

/**
 * A thumb that is not moving stops the record dead. **This is contact, not motion.**
 *
 * `HTMLMediaElement.playbackRate = 0` halts rendering without pausing the element:
 * `paused` stays `false`, no `pause` event fires, the store is not touched and the
 * release path is a plain rate write again. Pausing the element instead would flip the
 * transport button, the OS lock screen and `Disc`'s own `playing` for the length of a
 * gesture, and would need a `play()` — a promise that can reject — to undo.
 *
 * Both `engine/events.ts` and `engine/mediasession.ts` already coerce a zero rate to
 * `1` before `setPositionState`, which throws on one; that guard predates this and is
 * what makes a zero safe to write here.
 */
export const HELD_RATE = 0;

/**
 * How long the record keeps rolling after the thumb stops moving, in milliseconds.
 *
 * The scratch rate is a property of MOVEMENT, not of contact — the defect this fixes
 * (operator, phone, 2026-09-06: *"если просто держать её, трек начинает играть с
 * медленной скоростью… раньше трек останавливался"*) was `onPointerDown` writing
 * {@link SCRUB_RATE} on contact and nothing writing it back until release.
 *
 * 120 ms is one and a third {@link SCRUB_SEEK_INTERVAL_MS} windows: long enough that
 * the gaps between real pointer events inside a slow drag never stop the platter, short
 * enough that a thumb coming to rest is silent before anyone calls it a bug.
 */
export const HOLD_SETTLE_MS = 120;

/**
 * At most one `currentTime` write per this many milliseconds while the thumb is down.
 *
 * **THIS IS THE FIX FOR "звук просто пропадает"** (operator, real phone, 2026-09-05).
 * `onPointerMove` used to assign `deck.currentTime` on every single pointer event —
 * 60–120 Hz in Telegram's webview. Every assignment starts the media element's seek
 * algorithm and ABORTS the seek already in flight, and the audio here is a byte-range
 * stream proxied from Telegram (`apps/api/src/routes/stream.ts`, whose own comment
 * reads "a single seek can produce dozens of range requests inside one second"). The
 * element spent the entire gesture seeking and never rendered a buffer: silence under
 * the thumb, sound back the instant it lifted. On a laptop with the file warm in cache
 * the same code sounds fine, which is why this only ever reproduced on the phone.
 *
 * 90 ms is ~11 writes a second; at {@link SCRUB_RATE} each window renders ~70 ms of
 * audio before the next jump, which is what a hand on a platter actually sounds like.
 * It needs no `AudioContext` (§7.3, §9.6 — Telegram denies one) and no decoded
 * `AudioBuffer` (~22 MiB per audio-minute).
 */
export const SCRUB_SEEK_INTERVAL_MS = 90;

/** …and only when the thumb has moved at least this much audio since the last seek. */
export const SCRUB_MIN_STEP_S = 0.05;

/** §7.3's two gain ramps, in milliseconds: after a pull-up, and after a spring-back. */
export const RAMP_PULL_UP_MS = 200;
export const RAMP_SPRING_MS = 120;

/** How long the slammed `PULL UP!` stamp stays on the bed before the bed resets. */
const SLAM_HOLD_MS = 1200;

/** §7.2 NEEDLE DROP is `--tdrop`; the attribute comes off again when it has played. */
const NEEDLE_DROP_MS = 500;

/** A 7" is cut at 45 and played down at 33. `45` is nominal. */
const NOMINAL_SPEED: TransportSpeed = 45;

/** The eyebrow, left-flush under the top rule (§8.1). Latin caps: real label print. */
export const EYEBROW = 'CH. DANCEHALL';

/** The reduced-motion pull-up needs a Russian accessible name (§11, §9.4). */
export const PULL_UP_LABEL = 'Отмотать в начало';

/** The screen's own name, for the landmark a screen reader announces. */
export const SCREEN_LABEL = 'Сейчас играет';

/**
 * The vocabulary a dub or instrumental cut is actually catalogued under — the words
 * the scene uses, never invented patois (§11). A sibling post whose title carries one
 * of these is the `VERSION` the transport chip jumps to.
 */
const VERSION_MARKERS = /(?:^|[\s([–—-])(version|dub|instrumental|dubwise|версия|инструментал)\b/iu;

/* ══ pure helpers ══════════════════════════════════════════════════════════════════ */

/** `true` when a title reads as the dub / instrumental cut of something. */
export function isVersionCut(title: string): boolean {
  return VERSION_MARKERS.test(title);
}

/**
 * The dub or instrumental cut of this track, or `null`.
 *
 * `siblings` is the rest of the channel post's group — the same `groupedId`, which is
 * how a plant posts an A side and its version together — so this is a catalogue fact,
 * not a guess about the audio. When the catalogue has no version the chip renders
 * disabled and `--text-3` rather than disappearing, so the strip does not reflow
 * between tracks (§8.1).
 */
export function findVersionCut(detail: TrackDetail | null): TrackSummary | null {
  if (detail === null) return null;
  return detail.siblings.find((sibling) => isVersionCut(sibling.title)) ?? null;
}

/**
 * `PRESS` — how this copy was pressed, from the container the channel actually sent.
 *
 * Derived from the MIME type, falling back to the file extension, and `null` when
 * neither says anything: an unknown pressing prints no row at all (§12.6).
 */
export function pressingOf(
  mimeType: string | null | undefined,
  fileName: string | null | undefined,
): string | null {
  const mime = (mimeType ?? '').toLowerCase();
  const known: Readonly<Record<string, string>> = {
    'audio/mpeg': 'MP3',
    'audio/mp3': 'MP3',
    'audio/flac': 'FLAC',
    'audio/x-flac': 'FLAC',
    'audio/mp4': 'M4A',
    'audio/aac': 'AAC',
    'audio/ogg': 'OGG',
    'audio/opus': 'OPUS',
    'audio/wav': 'WAV',
    'audio/x-wav': 'WAV',
  };
  const byMime = known[mime];
  if (byMime !== undefined) return byMime;

  const dot = (fileName ?? '').lastIndexOf('.');
  if (dot > 0) {
    const extension = (fileName ?? '').slice(dot + 1).toUpperCase();
    if (/^[A-Z0-9]{2,5}$/.test(extension)) return extension;
  }

  const slash = mime.indexOf('/');
  if (slash > 0 && slash < mime.length - 1) return mime.slice(slash + 1).toUpperCase();
  return null;
}

/**
 * `ADDED` — the channel post date as `DD.MM.YYYY`, in tabular mono.
 *
 * Formatted by hand rather than through `Intl`: the digits are the point, the
 * separator is a full stop in Russian, and a locale-dependent string would make the
 * column jitter between clients.
 */
export function formatAdded(unixSeconds: number | null | undefined): string | null {
  if (typeof unixSeconds !== 'number' || !Number.isFinite(unixSeconds) || unixSeconds <= 0) {
    return null;
  }
  const date = new Date(unixSeconds * 1000);
  if (Number.isNaN(date.getTime())) return null;
  const dd = String(date.getUTCDate()).padStart(2, '0');
  const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
  return `${dd}.${mm}.${String(date.getUTCFullYear())}`;
}

/** The digits of `BL-0421`, for the archive numeral. `null` when there are none. */
export function archiveNumeral(catalogueNo: string | null | undefined): string | null {
  const digits = /(\d+)\s*$/.exec(catalogueNo ?? '');
  return digits === null ? null : (digits[1] ?? null);
}

/** The first tag of a kind, or `null`. Absent metadata is omitted, never dashed. */
function tagOf(detail: TrackDetail | null, kind: 'riddim' | 'label'): string | null {
  if (detail === null) return null;
  return detail.tags.find((tag) => tag.kind === kind)?.name ?? null;
}

/** Clamp into `[0, 360)` and fold to the shortest way round, in degrees. */
function shortestArc(delta: number): number {
  let value = delta;
  while (value > 180) value -= 360;
  while (value <= -180) value += 360;
  return value;
}

/**
 * `preservesPitch` is off whenever the deck is off nominal — that is the joke.
 *
 * **The tolerance is kept on purpose, though nothing needs it today.** It used to be
 * `rate === 1`, which is exact and correct while the only two rates are `1` and
 * `33/45` — both literals, which is again the case since the pitch fader was removed
 * on 2026-09-06. It became wrong the moment a varispeed trim made the rate a *product*
 * (`base × (1 + percent / 100)`): a float product that *should* be one is not always
 * `1`, so a deck walked back to nominal by arithmetic would never lock its pitch again
 * and the tune would sit a hair sharp for the rest of the session with nothing on
 * screen to explain it.
 *
 * 1e-9 is far below any step a trim would produce (0.5 % is 5e-3) and far above
 * double-precision dust, so it can only ever forgive the second. Leaving it costs
 * nothing and it is the one line that would otherwise have to be rediscovered when a
 * rate control comes back — see `wiki/pages/pitch-next-attempt.md`.
 */
function pitchPreservedAt(rate: number): boolean {
  return Math.abs(rate - 1) < 1e-9;
}

/* ══ the deck surface ══════════════════════════════════════════════════════════════ */

/**
 * The four element properties this screen writes directly.
 *
 * `app/wiring` is the one place the engine and the stores meet, and everything that
 * can go through it does: `seekTo`, `restart`, `setVolume`, `toggle`, `next`,
 * `previous`. But the `Wiring` interface exposes no rate control, and a pull-up has to
 * write `currentTime` at pointer rate without a store round trip per frame — so those
 * two properties are written on the element itself, through this narrow structural
 * type, which is also what makes the gesture testable against a plain object.
 */
export interface PullUpDeck {
  currentTime: number;
  playbackRate: number;
  volume: number;
  preservesPitch?: boolean;
  /** Seeks to the nearest already-decoded frame instead of decoding to an exact time. */
  fastSeek?(time: number): void;
}

/** The commands this screen issues. A subset of {@link Wiring}, so a fake is small. */
export type NowPlayingCommands = Pick<
  Wiring,
  'play' | 'toggle' | 'next' | 'previous' | 'seekTo' | 'restart' | 'setVolume'
>;

/** The endpoints this screen reads: `GET /api/tracks/:id` and `/peaks` (§13.10). */
export type NowPlayingClient = Pick<ApiClient, 'track' | 'peaks' | 'mediaUrl' | 'ensureMediaUrls'>;

export interface NowPlayingDeps {
  readonly wiring?: NowPlayingCommands;
  readonly client?: NowPlayingClient;
  readonly deck?: PullUpDeck;
  readonly telegram?: TelegramFacade;
  /** The favourites slice behind the filing bracket. Injected so a test can seed it. */
  readonly library?: typeof useLibraryStore;
  /**
   * Overrides the media query. `undefined` reads `prefers-reduced-motion` from the
   * platform, which is the only thing the app itself ever does.
   */
  readonly reducedMotion?: boolean;
}

/**
 * `ScreenProps` from `app/router`, with every field optional so the screen can also be
 * rendered directly — by a test, or by the mini player's expand — and with the
 * dependency seam the gesture and the fetch need.
 */
export interface NowPlayingProps {
  readonly route?: RouteName;
  readonly params?: RouteParams;
  readonly deps?: NowPlayingDeps;
}

/* ══ reduced motion ════════════════════════════════════════════════════════════════ */

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

/**
 * Whether the platform asks for reduced motion — read live, because a user may change
 * it while the app is open and the pull-up must appear and disappear with it.
 *
 * `matchMedia` is absent in some webviews and in the test harness before its shim
 * lands, so both the read and the subscription are guarded and default to "animated",
 * which is the state the rest of the design assumes.
 */
function useReducedMotion(override: boolean | undefined): boolean {
  const [system, setSystem] = useState<boolean>(() => queryReducedMotion());

  useEffect(() => {
    if (override !== undefined) return;
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const list = window.matchMedia(REDUCED_MOTION_QUERY);
    const onChange = (): void => {
      setSystem(list.matches === true);
    };
    onChange();
    list.addEventListener?.('change', onChange);
    return () => {
      list.removeEventListener?.('change', onChange);
    };
  }, [override]);

  return override ?? system;
}

function queryReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia(REDUCED_MOTION_QUERY).matches === true;
}

/* ══ the screen ════════════════════════════════════════════════════════════════════ */

interface DragState {
  /** The pointer's angle about the record's centre when the thumb went down. */
  lastAngle: number;
  /** Accumulated travel, signed: anticlockwise is negative (screen coordinates). */
  delta: number;
  /** Total CLOCKWISE travel, unsigned — the pull-up's "how did it get there" guard. */
  forward: number;
  /** `currentTime` at pointer-down; the scrub is measured back from here. */
  startTime: number;
  /** Where the thumb is right now, in audio seconds. The release commits THIS. */
  targetTime: number;
  /** The last position actually written to the deck — the seek throttle's low-water mark. */
  seekedTime: number;
  /** `Date.now()` of the last write to the deck. `0` makes the first move seek at once. */
  lastSeekAt: number;
  /** The gain to ramp back to on release, captured before the duck. */
  volume: number;
  /** The rate to restore on release: 1, or 33/45 when the deck is playing at 33. */
  rate: number;
}

export function NowPlaying({ deps }: NowPlayingProps = {}): JSX.Element {
  const wiring = deps?.wiring ?? getWiring();
  const client = deps?.client ?? api;
  const deck: PullUpDeck = deps?.deck ?? audio;
  const tg = deps?.telegram ?? defaultTelegram;
  const library = deps?.library ?? useLibraryStore;
  const reducedMotion = useReducedMotion(deps?.reducedMotion);

  /* — state read — */
  const trackId = usePlayerStore((state) => state.trackId);
  const isPlaying = usePlayerStore((state) => state.isPlaying);
  const positionMs = usePlayerStore((state) => state.positionMs);
  const durationMs = usePlayerStore(selectEffectiveDurationMs);
  const isFullySeekable = usePlayerStore((state) => state.isFullySeekable);
  const playerError = usePlayerStore((state) => state.error);
  const volume = usePlayerStore((state) => state.volume);

  const track = useQueueStore(selectCurrentTrack);
  const queueIndex = useQueueStore((state) => state.index);
  const queueStatus = useQueueStore((state) => state.status);
  const queueCount = useQueueStore((state) => state.items.length);
  const shuffle = useQueueStore((state) => state.shuffle);
  const repeat = useQueueStore((state) => state.repeatMode);
  const setShuffle = useQueueStore((state) => state.setShuffle);
  const cycleRepeatMode = useQueueStore((state) => state.cycleRepeatMode);
  const enqueueNext = useQueueStore((state) => state.enqueueNext);

  /*
   * Subscribed to ONE id, not to the whole map: this screen shows one record, so the
   * derived boolean is the narrowest thing that still repaints the bracket when the
   * crate screen behind it files the same tune. `selectIsFavorite` is the store's only
   * sanctioned read — `track.isFavorite` is a stale copy the moment anything toggles
   * (`store/library.ts`'s header).
   */
  const filed = library((state) => (trackId === null ? false : selectIsFavorite(trackId)(state)));

  /* — local state — */
  const [detail, setDetail] = useState<TrackDetail | null>(null);
  const [peaks, setPeaks] = useState<Uint8Array | null>(null);
  const [coverUrl, setCoverUrl] = useState<string | null>(null);
  const [speed, setSpeed] = useState<TransportSpeed>(NOMINAL_SPEED);
  const [dragging, setDragging] = useState(false);
  const [slammed, setSlammed] = useState(false);
  const [loupe, setLoupe] = useState(false);
  const [needleDrop, setNeedleDrop] = useState(false);
  const [marquee, setMarquee] = useState(false);

  /* — refs — */
  const mountRef = useRef<HTMLDivElement | null>(null);
  const titleRef = useRef<HTMLHeadingElement | null>(null);
  const dragRef = useRef<DragState | null>(null);
  const volumeRef = useRef(volume);
  const rampRef = useRef<ReturnType<typeof setInterval> | null>(null);
  /** The one timer that stops the platter after the thumb comes to rest. */
  const settleRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  /** Which record was filed by hand, and when — see the detail fetch's seed guard. */
  const filedByHandRef = useRef<{ id: number; at: number } | null>(null);

  useEffect(() => {
    /* Mirrored after render rather than read from a closure: a pointer handler built
       three renders ago would otherwise restore a stale gain. */
    if (dragRef.current === null) volumeRef.current = volume;
  }, [volume]);

  /** Every timeout this screen owns, cleared together on unmount. */
  const later = useCallback((fn: () => void, ms: number): void => {
    const id = setTimeout(() => {
      timersRef.current = timersRef.current.filter((each) => each !== id);
      fn();
    }, ms);
    timersRef.current = [...timersRef.current, id];
  }, []);

  useEffect(
    () => () => {
      for (const id of timersRef.current) clearTimeout(id);
      timersRef.current = [];
      if (rampRef.current !== null) {
        clearInterval(rampRef.current);
        rampRef.current = null;
      }
      /* A settle timer that outlives the screen would write a zero rate onto an
         element the next screen is playing through. */
      if (settleRef.current !== null) {
        clearTimeout(settleRef.current);
        settleRef.current = null;
      }
      /* The safety net of §7.3: a drag that outlives its component still gives
         Telegram's vertical-swipe gesture back. */
      if (dragRef.current !== null) {
        dragRef.current = null;
        tg.enableVerticalSwipes();
      }
    },
    [tg],
  );

  /* ── the detail fetch ───────────────────────────────────────────────────────── */

  useEffect(() => {
    if (trackId === null) {
      setDetail(null);
      setPeaks(null);
      setCoverUrl(null);
      return;
    }

    const controller = new AbortController();
    const startedAt = Date.now();
    let live = true;

    const load = async (): Promise<void> => {
      const got = await client.track(trackId, { signal: controller.signal });
      if (!live) return;
      setDetail(got);

      /*
       * The detail is the server's word on whether this record is filed, and it is the
       * only fresh one this screen gets: a queue restored from `store/persist` carries
       * a `TrackSummary` whose `isFavorite` was true when it was cached, so seeding the
       * map from the queue would resurrect a record the user removed on another device.
       *
       * The guard is for the one race that answer has: the response was computed BEFORE
       * the request left, so a bracket tapped while it was in flight would be undone by
       * an answer that predates the tap. `toggleFavorite` has already written the truth
       * optimistically and is talking to the same server; the stale answer is dropped.
       */
      const byHand = filedByHandRef.current;
      if (byHand === null || byHand.id !== trackId || byHand.at < startedAt) {
        library.getState().noteTracks([got]);
      }

      /* The detail already carries the peaks; `/peaks` is the fallback for a row whose
         waveform landed after the detail was cached. Never a synthesised signal. */
      let encoded = got.peaksB64;
      if (encoded === null && got.hasPeaks) {
        const response = await client.peaks(trackId, { signal: controller.signal });
        if (!live) return;
        encoded = response.peaks;
      }
      setPeaks(encoded === null ? null : decodePeaks(encoded));

      /* Artwork is composited INSIDE the label only (§6.5, §12.22). A cached cover
         needs a signed media URL; a stripped thumbnail is already inline and costs no
         request at all. */
      if (got.cover.state === 'cached') {
        const minted = await client.ensureMediaUrls([trackId], 'cover');
        if (!live) return;
        setCoverUrl(minted[trackId] ?? client.mediaUrl(trackId, 'cover'));
        return;
      }
      setCoverUrl(
        got.cover.blurB64 === null ? null : `data:image/jpeg;base64,${got.cover.blurB64}`,
      );
    };

    void load().catch(() => {
      /* The summary is enough to play and to print the plate: a failed detail leaves a
         shorter spec table, never a blank screen (§8.8). */
    });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client, library, trackId]);

  /* ── the needle drop: one-shot, never on resume (§7.2) ──────────────────────── */

  const dropNeedle = useCallback((): void => {
    setNeedleDrop(true);
    later(() => {
      setNeedleDrop(false);
    }, NEEDLE_DROP_MS);
  }, [later]);

  const playingRef = useRef(isPlaying);
  useEffect(() => {
    playingRef.current = isPlaying;
  }, [isPlaying]);

  useEffect(() => {
    /* Keyed on the track alone: a play-from-paused on the same record is a resume and
       must not re-drop the needle. */
    if (trackId === null || !playingRef.current) return;
    dropNeedle();
  }, [trackId, dropNeedle]);

  /* ── the marquee: one pass, only when the 3-line clamp is hiding something ──── */

  useLayoutEffect(() => {
    const node = titleRef.current;
    if (node === null) {
      setMarquee(false);
      return;
    }
    const hidden = node.scrollHeight - node.clientHeight;
    if (hidden > 1) {
      node.style.setProperty('--marquee-shift', `${String(hidden)}px`);
      setMarquee(true);
      return;
    }
    node.style.removeProperty('--marquee-shift');
    setMarquee(false);
  }, [track?.title]);

  /* ── the deck: SPEED ───────────────────────────────────────────────────────── */

  /**
   * The gear, and the only rate control on this screen.
   *
   * A ±8 % varispeed trim shipped beside it on 2026-09-05 and was removed on
   * 2026-09-06 at the operator's request ("вообще питч убери"): he could not tell
   * what the control was, a Technics-style redesign was rejected, and the answer was
   * to take it off the screen rather than to keep iterating on it. The engine below
   * still treats the rate as a product of one term, so putting a trim back is a
   * multiplication and not a rewrite. `wiki/pages/pitch-next-attempt.md` holds the two
   * measured directions that were never built.
   */
  const baseRate = speed === NOMINAL_SPEED ? 1 : 33 / 45;

  /**
   * The live rate, mirrored for the pull-up's release path.
   *
   * `endDrag` used to restore `drag.rate`, captured at pointer-down. That is wrong even
   * with only SPEED on the screen — a chip press cannot happen mid-scratch, but the
   * mirrored ref costs nothing and it is what any second rate control would need. It
   * was a live defect while the pitch fader shipped, because `.times` sits outside
   * `.deck` and a second finger could reach it mid-scratch.
   */
  const rateRef = useRef(baseRate);

  useEffect(() => {
    rateRef.current = baseRate;
    if (dragRef.current !== null) return;
    deck.playbackRate = baseRate;
    deck.preservesPitch = pitchPreservedAt(baseRate);
  }, [baseRate, deck]);

  /* ── the gain ramp (§7.3). Stepped on a timer, never on rAF ────────────────── */

  const rampVolume = useCallback(
    (to: number, ms: number): void => {
      if (rampRef.current !== null) clearInterval(rampRef.current);
      const steps = 8;
      const from = deck.volume;
      let step = 0;
      rampRef.current = setInterval(
        () => {
          step += 1;
          const value = from + (to - from) * (step / steps);
          wiring.setVolume(step >= steps ? to : value);
          if (step >= steps && rampRef.current !== null) {
            clearInterval(rampRef.current);
            rampRef.current = null;
          }
        },
        Math.max(1, Math.round(ms / steps)),
      );
    },
    [deck, wiring],
  );

  /* ── THE PULL UP ───────────────────────────────────────────────────────────── */

  /**
   * Stop the platter: a hand on a record holds it still, it does not slow it down.
   *
   * Cancels any pending settle so the two writes can never race — the timer fires and
   * finds nothing to do, rather than landing a zero after the thumb has lifted.
   */
  const holdPlatter = useCallback((): void => {
    if (settleRef.current !== null) {
      clearTimeout(settleRef.current);
      settleRef.current = null;
    }
    deck.playbackRate = HELD_RATE;
    deck.preservesPitch = false;
  }, [deck]);

  /**
   * The thumb moved, so the groove moves under it: the audible scrub rate, and a fresh
   * {@link HOLD_SETTLE_MS} countdown back to a stopped platter.
   */
  const scrubPlatter = useCallback((): void => {
    if (deck.playbackRate !== SCRUB_RATE) {
      deck.playbackRate = SCRUB_RATE;
      deck.preservesPitch = false;
    }
    if (settleRef.current !== null) clearTimeout(settleRef.current);
    settleRef.current = setTimeout(() => {
      settleRef.current = null;
      /* Only while the thumb is still down: a release restores the deck's own rate on
         its own path, and this must not undo it. */
      if (dragRef.current === null) return;
      deck.playbackRate = HELD_RATE;
      deck.preservesPitch = false;
    }, HOLD_SETTLE_MS);
  }, [deck]);

  /** The pointer's angle about the record's centre, in degrees, or `null`. */
  const angleAt = useCallback((clientX: number, clientY: number): number | null => {
    const node = mountRef.current;
    if (node === null) return null;
    const rect = node.getBoundingClientRect();
    /* No layout means no centre to measure from; the drag simply does not start
       rather than snapping the record to an imaginary origin. */
    if (rect.width <= 0 || rect.height <= 0) return null;
    const cx0 = rect.left + rect.width / 2;
    const cy0 = rect.top + rect.height / 2;
    return (Math.atan2(clientY - cy0, clientX - cx0) * 180) / Math.PI;
  }, []);

  const endDrag = useCallback(
    (commit: boolean): void => {
      const drag = dragRef.current;
      if (drag === null) return;
      dragRef.current = null;
      if (settleRef.current !== null) {
        clearTimeout(settleRef.current);
        settleRef.current = null;
      }

      /* Order matters: the platform gesture comes back before anything else can go
         wrong, and the rate is restored on THIS path and on every other one. */
      tg.enableVerticalSwipes();
      /* `rateRef`, not `drag.rate`: the fader may have moved while the record was
         under a thumb, and the release must give the deck back the rate the screen
         is showing, not the one it had when the scratch started. */
      deck.playbackRate = rateRef.current;
      deck.preservesPitch = pitchPreservedAt(rateRef.current);
      mountRef.current?.style.setProperty('--angle', '0deg');
      setDragging(false);

      const pulled =
        commit && drag.delta <= PULL_UP_THRESHOLD_DEG && drag.forward <= PULL_UP_MAX_FORWARD_DEG;
      if (pulled) {
        tg.haptics.impact('medium');
        setSlammed(true);
        later(() => {
          setSlammed(false);
        }, SLAM_HOLD_MS);
        void wiring.restart();
        dropNeedle();
        rampVolume(drag.volume, RAMP_PULL_UP_MS);
        return;
      }

      /*
       * A spring-back is not a seek: the record returns to where it was, so the store
       * is told where the scrub actually left the playhead — and the playhead is
       * `drag.targetTime`, NOT `deck.currentTime`. The two are the same only when
       * every move seeked; now that the seek is throttled the element trails the thumb
       * by up to one {@link SCRUB_SEEK_INTERVAL_MS} window, and committing the
       * element's value would land the track slightly behind where the record is
       * visibly sitting. This one line is what "the sound is tied to the record" means
       * on release.
       */
      wiring.seekTo(drag.targetTime * 1000);
      rampVolume(drag.volume, RAMP_SPRING_MS);
    },
    [deck, dropNeedle, later, rampVolume, tg, wiring],
  );

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>): void => {
      if (dragRef.current !== null) return;
      const angle = angleAt(event.clientX, event.clientY);
      if (angle === null) return;

      tg.disableVerticalSwipes();
      event.currentTarget.setPointerCapture?.(event.pointerId);

      const startTime = deck.currentTime;
      dragRef.current = {
        lastAngle: angle,
        delta: 0,
        forward: 0,
        startTime,
        targetTime: startTime,
        seekedTime: startTime,
        lastSeekAt: 0,
        volume: volumeRef.current,
        rate: baseRate,
      };

      /* Contact stops the record; only movement gives it a rate (§7.3, and
         {@link HOLD_SETTLE_MS} for the defect this replaced). */
      holdPlatter();
      if (rampRef.current !== null) {
        clearInterval(rampRef.current);
        rampRef.current = null;
      }
      wiring.setVolume(DUCK_VOLUME);
      setDragging(true);
    },
    [angleAt, baseRate, deck, holdPlatter, tg, wiring],
  );

  const onPointerMove = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>): void => {
      const drag = dragRef.current;
      if (drag === null) return;
      const angle = angleAt(event.clientX, event.clientY);
      if (angle === null) return;

      const step = shortestArc(angle - drag.lastAngle);
      drag.delta += step;
      if (step > 0) drag.forward += step;
      drag.lastAngle = angle;

      /* Movement, and only movement, gives the deck a rate. A pointer event that
         carries no travel — a pressure change, a coalesced repeat — leaves the platter
         exactly as still as the thumb is. */
      if (step !== 0) scrubPlatter();

      /* Written straight onto the node: 1:1 rotation at pointer rate must not cost a
         React render per frame (`Disc` forwards its ref for exactly this). */
      mountRef.current?.style.setProperty('--angle', `${String(drag.delta)}deg`);

      const back = (-drag.delta / 360) * AUDIO_PER_REV;
      drag.targetTime = Math.max(0, drag.startTime - back);

      /*
       * THE ROTATION IS 1:1, THE SEEK IS NOT — and that asymmetry is the whole fix.
       *
       * The `--angle` write above still happens on every pointer event, so the record
       * stays glued to the thumb. The `currentTime` write does not: see
       * {@link SCRUB_SEEK_INTERVAL_MS} for why writing it per event produced silence.
       * The second condition suppresses a seek the listener could not hear anyway —
       * a thumb resting still on the record re-seeking eleven times a second is eleven
       * decoder flushes for nothing.
       *
       * `fastSeek` where it exists (WebKit, Gecko): it lands on the nearest already
       * decoded frame instead of decoding down to an exact time, which is precisely
       * the trade a scratch wants. Chromium does not implement it and falls through to
       * the plain assignment, which the throttle has already made affordable.
       */
      const at = Date.now();
      if (
        at - drag.lastSeekAt < SCRUB_SEEK_INTERVAL_MS ||
        Math.abs(drag.targetTime - drag.seekedTime) < SCRUB_MIN_STEP_S
      ) {
        return;
      }
      drag.lastSeekAt = at;
      drag.seekedTime = drag.targetTime;
      if (typeof deck.fastSeek === 'function') deck.fastSeek(drag.targetTime);
      else deck.currentTime = drag.targetTime;
    },
    [angleAt, deck, scrubPlatter],
  );

  const onPointerUp = useCallback((): void => {
    endDrag(true);
  }, [endDrag]);

  const onPointerCancel = useCallback((): void => {
    endDrag(false);
  }, [endDrag]);

  const onLostPointerCapture = useCallback((): void => {
    endDrag(false);
  }, [endDrag]);

  const restartFromStamp = useCallback((): void => {
    tg.haptics.impact('medium');
    void wiring.restart();
    dropNeedle();
  }, [dropNeedle, tg, wiring]);

  /* ── transport ─────────────────────────────────────────────────────────────── */

  const onPlayPause = useCallback((): void => {
    tg.haptics.impact('rigid');
    void wiring.toggle();
  }, [tg, wiring]);

  const onPrev = useCallback((): void => {
    tg.haptics.impact('rigid');
    void wiring.previous();
  }, [tg, wiring]);

  const onNext = useCallback((): void => {
    tg.haptics.impact('rigid');
    void wiring.next();
  }, [tg, wiring]);

  const onShuffle = useCallback((): void => {
    tg.haptics.impact('light');
    setShuffle(!shuffle);
  }, [setShuffle, shuffle, tg]);

  const onRepeat = useCallback((): void => {
    tg.haptics.impact('light');
    cycleRepeatMode();
  }, [cycleRepeatMode, tg]);

  const onSpeed = useCallback((): void => {
    tg.haptics.impact('light');
    setSpeed((current) => (current === NOMINAL_SPEED ? 33 : NOMINAL_SPEED));
  }, [tg]);

  const version = useMemo(() => findVersionCut(detail), [detail]);

  const onVersion = useCallback((): void => {
    if (version === null) return;
    tg.haptics.impact('light');
    void enqueueNext([version]).then(() => wiring.next());
  }, [enqueueNext, tg, version, wiring]);

  const onSeek = useCallback(
    (seconds: number): void => {
      wiring.seekTo(seconds * 1000);
    },
    [wiring],
  );

  const onScrubStart = useCallback((): void => {
    setLoupe(true);
  }, []);

  const onScrubEnd = useCallback((): void => {
    setLoupe(false);
  }, []);

  /*
   * The filing bracket (§8.5, §12.21 — `[ ]`, never a heart). `toggleFavorite` is
   * optimistic and rolls itself back on failure, so nothing here waits on the network:
   * the mark fills under the thumb and the haptic matches every other file in the app.
   */
  const onFile = useCallback((): void => {
    if (trackId === null) return;
    filedByHandRef.current = { id: trackId, at: Date.now() };
    tg.haptics.impact('light');
    void library.getState().toggleFavorite(trackId);
  }, [library, tg, trackId]);

  const goToCrate = useCallback((): void => {
    navigate(buildPath('crate'));
  }, []);

  /* ── the two states that replace the content, on the same grid ─────────────── */

  if (track === null) {
    const body =
      queueStatus === 'loading' && queueCount === 0 ? (
        <Loading grid="plate" />
      ) : (
        <Empty variant="queue" grid="plate" onAction={goToCrate} />
      );
    return (
      <div className={styles.screen} data-screen="now">
        {body}
      </div>
    );
  }

  /* ── derived print values ──────────────────────────────────────────────────── */

  const durationS = durationMs > 0 ? durationMs / 1000 : track.durationS;
  const positionS = Math.min(Math.max(positionMs / 1000, 0), durationS > 0 ? durationS : Infinity);
  const remainingS = Math.max(0, durationS - positionS);
  const messageId = detail?.channel.messageId ?? track.id;
  const numeral = archiveNumeral(track.catalogueNo);
  const position = queueIndex >= 0 ? String(queueIndex + 1).padStart(3, '0') : null;
  const eyebrow = position === null ? EYEBROW : `${EYEBROW} · ${position}`;

  /*
   * RPM and BPM both follow the fader, and both go back to printing a whole number the
   * moment it is at rest.
   *
   * A decimal is how the plate says "someone is holding this". At `0.0` the row reads
   * `45` — a catalogue fact, the speed the record is cut at. Off nominal it reads
   * `46.6` — a measurement of what the platter is doing right now. Same for BPM: the
   * catalogue's own integer while the fader is home, one decimal while it is not.
   *
   * ## Where the tempo comes from, and how the plate says which
   *
   * Two different facts, never merged into one:
   *
   *  · `track.bpm` — the catalogue's own. `null` for all 115 live tracks today and
   *    staying that way until a tag, a caption or a person supplies one (§11.6;
   *    `0001_init.sql:96` "Never guessed"). Printed bare.
   *  · `detail.bpmMeasured` — what an onset analyser heard, written only where two
   *    independent passes over the same track agreed (`0003_bpm_measured.sql`).
   *    Printed with a leading `~`, because a measurement and a catalogue entry are
   *    not the same claim and the reader is entitled to know which one is on the
   *    plate.
   *
   * When neither exists the row does not print at all: `SpecTable` drops a missing
   * value, so the plate is one line shorter rather than showing a dash or a number
   * nobody can check.
   */
  const nominal = speed === NOMINAL_SPEED;
  const measuredBpm = detail?.bpmMeasured ?? null;
  const baseBpm = track.bpm ?? measuredBpm;
  const bpmMark = track.bpm === null && measuredBpm !== null ? '~' : '';

  const catalogue: SpecRow[] = [
    { label: 'CAT', value: track.catalogueNo },
    { label: 'SIDE', value: track.plate.side.replace(/^SIDE\s+/, '') },
    { label: 'RPM', value: String(speed) },
    {
      label: 'BPM',
      value:
        baseBpm === null
          ? null
          : `${bpmMark}${nominal ? String(baseBpm) : (baseBpm * baseRate).toFixed(1)}`,
    },
    { label: 'KEY', value: detail?.musicalKey ?? null },
  ];

  const spec: SpecRow[] = [
    { label: 'RIDDIM', value: tagOf(detail, 'riddim') },
    { label: 'LABEL', value: tagOf(detail, 'label') },
    { label: 'YEAR', value: track.year },
    { label: 'PRESS', value: pressingOf(detail?.mimeType, detail?.fileName) },
    { label: 'ADDED', value: formatAdded(track.postedAt) },
  ];

  const stamp = reducedMotion ? (
    /* §12.23: the pull-up never ships without this. */
    <Stamp
      copy="PULL UP!"
      edge="right"
      onPress={restartFromStamp}
      pressLabel={PULL_UP_LABEL}
      className={styles.stamp}
    />
  ) : slammed ? (
    <Stamp copy="PULL UP!" edge="right" slam className={styles.stamp} />
  ) : (
    <Stamp copy="DUBPLATE SPECIAL" edge="right" className={styles.stamp} />
  );

  return (
    <div
      className={styles.screen}
      data-screen="now"
      data-loupe={loupe ? 'true' : 'false'}
      data-pullup={dragging ? 'true' : 'false'}
    >
      <StateFrame grid="plate" className={styles.frame} ariaLabel={SCREEN_LABEL} role="group">
        <hr className={styles.top} data-top-rule="true" />

        <p className={cx('t-mono', 't-11', styles.eyebrow, styles.dim)} data-eyebrow="true">
          {eyebrow}
        </p>

        <div className={cx(styles.stage, styles.dim)}>
          <Halftone className={styles.halo}>
            <Disc
              playing={isPlaying && !dragging}
              needleDrop={needleDrop}
              ref={mountRef}
              className={styles.deck}
              pointer={
                reducedMotion
                  ? undefined
                  : {
                      onPointerDown,
                      onPointerMove,
                      onPointerUp,
                      onPointerCancel,
                      onLostPointerCapture,
                    }
              }
            >
              <RimText playing={isPlaying && !dragging} />
              <PlateLabel
                title={track.title}
                performer={track.performer}
                messageId={messageId}
                seed={track.plate.seed}
                catalogueNo={track.catalogueNo}
                coverUrl={coverUrl}
              />
            </Disc>
          </Halftone>

          <CropPlate crosshair className={styles.catalogue}>
            {/* `.catalogueSpec` narrows the label column to its own max-content: the
                shared `8.5ch` is measured in the 15px UI face, not the 11px mono the
                labels are set in, and inside this plate it left 36px for a catalogue
                number that needs 64. See the rule's comment. */}
            <SpecTable rows={catalogue} className={styles.catalogueSpec} />
          </CropPlate>
        </div>

        <div className={styles.head}>
          <div className={styles.headText}>
            <Ghost text={track.title}>
              <h1
                className={cx('t-display', 't-34', 't-clamp-3', styles.title, styles.dim)}
                ref={titleRef}
                data-marquee={marquee ? 'true' : 'false'}
              >
                {track.title}
              </h1>
            </Ghost>

            {track.performer === null ? null : (
              <p
                className={cx(
                  't-mono',
                  't-11',
                  styles.artist,
                  styles.dim,
                  hasCyrillic(track.performer) && styles.cyr,
                )}
                data-artist="true"
              >
                {track.performer}
              </p>
            )}
          </div>

          {/* Beside the title, in the `auto` column, because this screen's constraint is
              height (`wiki/pages/nowplaying-one-screen-budget.md`). Measured in Chromium
              at 393×770 and 375×600: the head is 49px with a performer and 113px with a
              3-line title, so a 44px target costs **0px** in both. The one exception is a
              one-line title with NO performer — 32 of 115 tracks — where the head is 32px
              and the bracket takes it to 44: **+12px**, and that is the whole price. */}
          <FileBracket filed={filed} onToggle={onFile} className={cx(styles.file, styles.dim)} />
        </div>

        <div className={styles.bandGroup}>
          <div className={styles.bandHead}>
            <Silkscreen>CUE</Silkscreen>
            {/* With no peaks the band draws its own ruler as the designed state, so a
                second one here would be two scales stacked (§6.7, §9.5). */}
            {peaks === null ? null : <Ruler variant="scale" className={styles.scale} />}
          </div>

          <Scrub
            durationS={durationS}
            positionS={positionS}
            peaks={peaks}
            onSeek={onSeek}
            onScrubStart={onScrubStart}
            onScrubEnd={onScrubEnd}
            disabled={!isFullySeekable}
          />

          <p className={styles.times}>
            <span className={cx('t-mono', 't-13', styles.elapsed)} data-time="elapsed">
              {formatTimecode(positionS)}
            </span>
            <span className={cx('t-mono', 't-13', styles.remaining)} data-time="remaining">
              {`−${formatTimecode(remainingS)}`}
            </span>
          </p>
        </div>

        <DownbeatRule className={cx(styles.dim, styles.downbeat)} />

        {playerError === null ? (
          <SpecTable rows={spec} className={cx(styles.spec, styles.dim)} />
        ) : (
          <ErrorState
            failure={playerError}
            grid="plate"
            onRetry={() => {
              void wiring.play();
            }}
            className={styles.dim}
          />
        )}

        <Transport
          className={styles.dim}
          playing={isPlaying}
          onPlayPause={onPlayPause}
          onPrev={onPrev}
          onNext={onNext}
          shuffle={shuffle}
          onShuffle={onShuffle}
          repeat={repeat}
          onRepeat={onRepeat}
          speed={speed}
          onSpeed={onSpeed}
          onVersion={version === null ? undefined : onVersion}
          versionActive={isVersionCut(track.title)}
        />
      </StateFrame>

      {numeral === null ? null : (
        <span
          className={cx('t-display', 't-64', styles.archive, styles.dim)}
          data-archive="true"
          aria-hidden="true"
        >
          {numeral}
        </span>
      )}

      <div className={cx(styles.bed, styles.dim)}>{stamp}</div>
    </div>
  );
}

/** The custom property the marquee measures into. Exported for the suite only. */
export const MARQUEE_SHIFT_PROPERTY = '--marquee-shift';

/** The custom property the pull-up writes at pointer rate. Exported for the suite. */
export const ANGLE_PROPERTY = '--angle';

/** Unused re-export guard: keeps the CSS-property type import honest. */
export type NowPlayingStyle = CSSProperties;
